import { TF } from 'react-translator-component';

import {
  InviteRejection,
  InviteRejectionResponse,
} from '../../../../types/responses/Interoperability';

/**
 * Tradução das recusas de token para a mensagem da tela (MV2-1716).
 *
 * Fica fora do componente porque é a parte da tela com mais regra e menos
 * marcação: cada motivo tem texto próprio, dois deles carregam data, e um deles
 * não pode contar o que sabe.
 */

const formatMoment = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return '';
  }

  return moment.toLocaleString();
};

/**
 * Mensagem para o motivo recusado.
 *
 * WRONG_TARGET não diz "este convite é de outro tenant": quem apresenta convite
 * alheio não deve descobrir que ele existe. Sai como token inválido — que é
 * exatamente o que ele é, para quem está apresentando.
 */
export const rejectionMessage = (
  rejection: InviteRejection,
  referenceDate: string | null,
): string => {
  const moment = formatMoment(referenceDate);

  switch (rejection) {
    case 'EXPIRED':
      return moment
        ? `${TF('This token expired on')} ${moment}. ${TF('Ask your partner for a new one.')}`
        : `${TF('This token has expired.')} ${TF('Ask your partner for a new one.')}`;

    case 'ALREADY_USED':
      return moment
        ? `${TF('This token was already used on')} ${moment}.`
        : TF('This token was already used.');

    case 'REVOKED':
      return TF('This invite was cancelled by whoever sent it.');

    case 'BLOCKED':
      return TF('Too many attempts. Wait a few minutes before trying again.');

    case 'INVALID':
    case 'WRONG_TARGET':
    default:
      return TF('Invalid token. Check whether you copied the whole code.');
  }
};

/**
 * Motivo dentro do erro do axios.
 *
 * A recusa chega como 400 (ou 429 no bloqueio) com o corpo da recusa. Qualquer
 * outra coisa — rede fora, 500 inesperado — não é recusa de token e não pode ser
 * anunciada como "token inválido": o usuário ficaria conferindo um token que
 * está correto.
 */
export const rejectionOf = (error: unknown): InviteRejectionResponse | null => {
  const body = (error as { response?: { data?: InviteRejectionResponse } })?.response?.data;

  return body?.rejection ? body : null;
};

/**
 * Mensagem de recusa que o serviço mandou, quando ela é para o usuário ler (MV2-1806).
 *
 * As recusas do gerar convite chegam como 400 do BadRequestException e já vêm
 * escritas para quem clicou: dizem qual conta não está habilitada a compartilhar,
 * que o token é da própria conta, que bidirecional exige app no parceiro. Trocar
 * isso por "tente novamente" tira do usuário a única informação que resolveria o
 * problema — ele não tem como adivinhar qual das contas do escopo recusou.
 *
 * O 5xx fica de fora: lá o corpo é o `ex.getMessage()` cru do handler genérico do
 * cross — texto de exceção interna, que não diz nada a quem clicou e ainda expõe
 * detalhe de implementação. Erro de rede também não tem `response`, e cai no
 * texto genérico de quem chama.
 */
export const refusalMessage = (error: unknown): string | null => {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })
    ?.response;

  if ((response?.status || 0) >= 500) {
    return null;
  }

  return response?.data?.message || null;
};
